import type { OrderSummary, OverviewReceiving } from '@/api/orders';
import type { StatusTone } from '@/lib/status';
import { rollupPackages } from './lib';
import { journeyAsksHref } from './askLinks';

export type NextActionCode =
  | 'archived' | 'delivery' | 'decide' | 'actions' | 'supplier' | 'setup'
  | 'documents' | 'duties' | 'receiving' | 'order' | 'balance' | 'done'
  | 'tracking' | 'pending' | 'none';

/**
 * The one thing a journey is waiting on, as a code the UI renders from. The
 * list column and the journey header both read this, so they can never
 * disagree — and no server sentence ever lands in either.
 */
export interface NextAction {
  code: NextActionCode;
  /** The fact, as the list column prints it. */
  label: string;
  /** Rendered with StatusText; see lib/status.ts for what each means. */
  tone: StatusTone;
  /** The button on the journey header; absent means nothing to press. */
  verb?: string;
  /** Where the verb goes: a route, or a tab on the journey itself. */
  to?: string;
  tab?: 'receiving';
}


export function nextOrderAction(
  order: OrderSummary,
  receivings: OverviewReceiving[],
  openActions = 0
): NextAction {
  if (order.archivedAt || ['cancelled', 'wont_order', 'closed'].includes(order.status)) {
    return { code: 'archived', label: order.archivedAt ? 'Archived' : 'No further action', tone: 'neutral' };
  }
  const tracking = rollupPackages(order.packages);
  if (tracking?.kind === 'problem') {
    return { code: 'delivery', label: tracking.label, tone: 'danger', verb: 'Check delivery', to: '/work/follow-ups' };
  }
  const disputed = receivings.reduce((n, r) => n + r.undecidedDiscrepancies, 0);
  if (disputed) {
    return {
      code: 'decide',
      label: `${disputed} count difference${disputed === 1 ? '' : 's'}`,
      tone: 'warning',
      verb: 'Decide',
      to: '/work/discrepancies',
    };
  }
  if (openActions > 0) {
    return {
      code: 'actions',
      label: `${openActions} decision${openActions === 1 ? '' : 's'} open`,
      tone: 'warning',
      verb: 'Answer',
      to: journeyAsksHref(order.id),
    };
  }
  if (order.newSupplierNeeded) {
    return { code: 'supplier', label: 'Supplier setup needed', tone: 'warning', verb: 'Set up', to: '/setup' };
  }
  if (order.needsSetup) {
    return { code: 'setup', label: 'Product setup needed', tone: 'warning', verb: 'Set up', to: '/setup' };
  }
  const blocked = receivings.find((r) => !r.balancedAt && (r.blockers.length || r.needs.length));
  if (blocked) {
    const need = blocked.needs[0];
    const label = need?.text || blocked.blockers[0]?.text || 'Receiving blocked';
    if (need?.code === 'duty_unattributed') {
      return { code: 'duties', label, tone: 'warning', verb: 'Open duties', to: '/work/duties' };
    }
    if (need && need.code.startsWith('invoice_')) {
      return { code: 'documents', label, tone: 'warning', verb: 'Open documents', to: '/documents' };
    }
    return { code: 'receiving', label, tone: 'warning', verb: 'Open receiving', tab: 'receiving' };
  }
  if (order.status === 'announced') {
    // The order-by date carries the urgency; the stage itself is a fact.
    return { code: 'order', label: 'To order', tone: 'neutral', verb: 'Mark ordered' };
  }
  if (receivings.some((r) => r.ready && !r.balancedAt)) {
    return { code: 'balance', label: 'Ready to balance', tone: 'success', verb: 'Balance', tab: 'receiving' };
  }
  if (order.allBalanced) return { code: 'done', label: 'All receivings balanced', tone: 'success' };
  if (tracking) return { code: 'tracking', label: tracking.label, tone: tracking.tone };
  if (order.openTasks) {
    return {
      code: 'pending',
      label: `${order.openTasks} pending task${order.openTasks === 1 ? '' : 's'}`,
      // With the run: someone else has it.
      tone: 'info',
    };
  }
  return { code: 'none', label: 'No action flagged', tone: 'neutral' };
}
